import type { AISettings, AIClient, AIAnalysisRequest, AIAnalysisResult } from '$lib/types/ai';
import { OpenAIClient } from './clients/OpenAIClient';
import { AnthropicClient } from './clients/AnthropicClient';
import { GoogleClient } from './clients/GoogleClient';
import { UpstageClient } from './clients/UpstageClient';
import { OpenRouterClient } from './clients/OpenRouterClient';

/**
 * AI Client Factory for Svelte Application
 * Adapted from reference/core/api/AIClientFactory.ts
 * Creates appropriate AI clients based on provider settings
 */
export class AIClientFactory {
  /**
   * Create AI client based on settings
   * @param settings AI configuration
   * @returns AI client instance
   */
  static createClient(settings: AISettings): AIClient {
    const modelName = settings.selectedModel;
    
    // Validate provider matches model and auto-correct if needed
    if (modelName.includes('/') && settings.provider !== 'openrouter') {
      // Silently use OpenRouter for slash-notation models
      return new OpenRouterClient(modelName, settings.maxTokens);
    }
    
    switch (settings.provider) {
      case 'openai':
        if (!settings.openaiApiKey) {
          throw new Error('OpenAI API key not configured');
        }
        return new OpenAIClient(settings.openaiApiKey, settings.selectedModel, settings.maxTokens);
      
      case 'anthropic':
        if (!settings.anthropicApiKey) {
          throw new Error('Anthropic API key not configured');
        }
        // Ensure we use the full model name
        const anthropicModel = settings.selectedModel || 'claude-3-5-sonnet-20241022';
        return new AnthropicClient(settings.anthropicApiKey, anthropicModel, settings.maxTokens);
      
      case 'google':
        if (!settings.googleApiKey) {
          throw new Error('Google API key not configured');
        }
        return new GoogleClient(settings.googleApiKey, settings.selectedModel, settings.maxTokens);
      
      case 'upstage':
        if (!settings.upstageApiKey) {
          throw new Error('Upstage API key not configured');
        }
        return new UpstageClient(settings.upstageApiKey, settings.selectedModel, settings.maxTokens);

      case 'openrouter':
        // All users use proxy (server applies rate limits by tier)
        return new OpenRouterClient(settings.selectedModel, settings.maxTokens);

      default:
        // Default to OpenRouter for unknown providers
        return new OpenRouterClient(settings.selectedModel, settings.maxTokens);
    }
  }

  /**
   * Check if API key is valid for provider
   * @param settings AI configuration
   * @returns API key validity
   */
  static hasValidApiKey(settings: AISettings): boolean {
    switch (settings.provider) {
      case 'openai':
        return !!settings.openaiApiKey;
      case 'anthropic':
        return !!settings.anthropicApiKey;
      case 'google':
        return !!settings.googleApiKey;
      case 'upstage':
        return !!settings.upstageApiKey;
      case 'openrouter':
        // OpenRouter uses server proxy, no client API key needed
        return true;
      default:
        return false;
    }
  }
}